<script>
export default { name: 'UiSelect' };
</script>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { ChevronDown, Search, X, Check } from 'lucide-vue-next';

let uidSeed = 0;

const props = defineProps({
  modelValue: { type: [String, Number, Array], default: '' },
  label: { type: String, default: '' },
  required: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Seleccionar...' },
  // Array de { value, label?, disabled? } o de strings simples.
  options: { type: Array, default: null },
  disabled: { type: Boolean, default: false },
  error: { type: String, default: '' },
  hint: { type: String, default: '' },
  // Componente lucide a mostrar a la izquierda, ej. :icon="Building"
  icon: { type: [Object, Function, String], default: null },
  multiple: { type: Boolean, default: false },
  id: { type: String, default: null },
  // Convierte el select en un combobox con búsqueda (typeahead sobre label y value).
  filterable: { type: Boolean, default: false },
  // Mensaje mostrado cuando la búsqueda no encuentra coincidencias (solo filterable).
  emptyText: { type: String, default: 'Sin resultados' },
});

const emit = defineEmits(['update:modelValue', 'change', 'focus', 'blur']);

const rootRef = ref(null);
const inputRef = ref(null);
const isOpen = ref(false);
const searchQuery = ref('');
const activeIndex = ref(-1);

const fieldId = props.id || `ui-select-${++uidSeed}`;

const selectClass = computed(() => [
  'w-full rounded-xl border bg-white dark:bg-slate-950 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 shadow-sm transition-all outline-none appearance-none focus:ring-4',
  props.error
    ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
    : 'border-slate-200 dark:border-slate-700 focus:border-brand-500 focus:ring-brand-500/10',
  props.icon ? 'pl-10' : '',
  'pr-10',
  props.disabled ? 'opacity-60 cursor-not-allowed' : '',
]);

const inputClass = computed(() => [
  'w-full rounded-xl border bg-white dark:bg-slate-950 pl-10 pr-16 py-2.5 text-sm text-slate-900 dark:text-slate-100 shadow-sm transition-all outline-none placeholder-slate-400 dark:placeholder-slate-600 focus:ring-4',
  props.error
    ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
    : 'border-slate-200 dark:border-slate-700 focus:border-brand-500 focus:ring-brand-500/10',
  props.disabled ? 'opacity-60 cursor-not-allowed' : '',
]);

const optValue = (opt) => (opt && typeof opt === 'object' ? opt.value : opt);
const optLabel = (opt) => (opt && typeof opt === 'object' ? (opt.label ?? opt.value) : opt);
const optDisabled = (opt) => !!(opt && typeof opt === 'object' && opt.disabled);
const optKey = (opt) =>
  opt && typeof opt === 'object' ? `${opt.value}:${opt.label ?? ''}` : String(opt);

function handleChange(event) {
  if (props.multiple) {
    emit('update:modelValue', Array.from(event.target.selectedOptions).map((o) => o.value));
  } else {
    emit('update:modelValue', event.target.value);
  }
}

// ---------------------------------------------------------------------------
// Modo filterable: combobox con búsqueda (typeahead) sobre label y value.
// ---------------------------------------------------------------------------

const normalizedOptions = computed(() => (Array.isArray(props.options) ? props.options : []));

const selectedValues = computed(() =>
  props.multiple ? (Array.isArray(props.modelValue) ? props.modelValue : []) : []
);

const hasSelection = computed(() => {
  if (props.multiple) return selectedValues.value.length > 0;
  return props.modelValue !== '' && props.modelValue !== null && props.modelValue !== undefined;
});

const selectedLabel = computed(() => {
  if (props.multiple) return '';
  const match = normalizedOptions.value.find(
    (opt) => String(optValue(opt)) === String(props.modelValue)
  );
  return match ? optLabel(match) : '';
});

const displayValue = computed(() => {
  if (searchQuery.value) return searchQuery.value;
  return props.multiple ? '' : selectedLabel.value;
});

const filteredOptions = computed(() => {
  const list = normalizedOptions.value;
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return list;
  return list.filter((opt) => {
    const label = String(optLabel(opt) ?? '').toLowerCase();
    const value = String(optValue(opt) ?? '').toLowerCase();
    return label.includes(q) || value.includes(q);
  });
});

const isSelected = (opt) => {
  const v = String(optValue(opt));
  return props.multiple ? selectedValues.value.includes(v) : String(props.modelValue) === v;
};

const chipLabel = (value) => {
  const match = normalizedOptions.value.find((opt) => String(optValue(opt)) === String(value));
  return match ? optLabel(match) : String(value);
};

function openDropdown() {
  if (props.disabled) return;
  isOpen.value = true;
}

function closeDropdown() {
  isOpen.value = false;
  activeIndex.value = -1;
}

function toggleDropdown() {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
}

function handleInput(event) {
  searchQuery.value = event.target.value;
  isOpen.value = true;
  activeIndex.value = -1;
}

function selectOption(opt) {
  if (optDisabled(opt)) return;
  if (props.multiple) {
    const val = String(optValue(opt));
    const current = selectedValues.value;
    const alreadySelected = current.some((v) => String(v) === val);
    const next = alreadySelected
      ? current.filter((v) => String(v) !== val)
      : [...current, val];
    emit('update:modelValue', next);
    emit('change', { value: next, option: opt });
  } else {
    emit('update:modelValue', optValue(opt));
    emit('change', { value: optValue(opt), option: opt });
    searchQuery.value = '';
    closeDropdown();
  }
}

function removeChip(value) {
  if (props.disabled) return;
  const next = selectedValues.value.filter((v) => String(v) !== String(value));
  emit('update:modelValue', next);
  emit('change', { value: next });
}

function clearSelection() {
  if (props.disabled) return;
  emit('update:modelValue', props.multiple ? [] : '');
  emit('change', { value: props.multiple ? [] : '' });
  searchQuery.value = '';
  inputRef.value?.focus();
}

function onKeydown(event) {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    openDropdown();
    activeIndex.value = Math.min(activeIndex.value + 1, filteredOptions.value.length - 1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    activeIndex.value = Math.max(activeIndex.value - 1, 0);
  } else if (event.key === 'Enter' && isOpen.value && filteredOptions.value.length) {
    event.preventDefault();
    const idx = activeIndex.value >= 0 ? activeIndex.value : 0;
    selectOption(filteredOptions.value[idx]);
  } else if (event.key === 'Escape' || event.key === 'Tab') {
    closeDropdown();
  }
}

function onClickOutside(event) {
  if (rootRef.value && !rootRef.value.contains(event.target)) {
    closeDropdown();
    searchQuery.value = '';
  }
}

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onBeforeUnmount(() => document.removeEventListener('mousedown', onClickOutside));
</script>

<template>
  <div>
    <label v-if="label" :for="fieldId" class="block text-xs font-semibold text-slate-500 uppercase mb-1">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <!-- MODO NATIVO (sin búsqueda): conserva el comportamiento original -->
    <div v-if="!filterable" class="relative">
      <component
        v-if="icon"
        :is="icon"
        class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
        aria-hidden="true"
      />

      <select
        :id="fieldId"
        :value="props.modelValue"
        :disabled="disabled"
        :multiple="multiple"
        :class="selectClass"
        @change="handleChange"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      >
        <option v-if="placeholder && !options" value="" disabled>{{ placeholder }}</option>
        <template v-if="options">
          <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
          <option
            v-for="opt in options"
            :key="optKey(opt)"
            :value="optValue(opt)"
            :disabled="optDisabled(opt)"
          >
            {{ optLabel(opt) }}
          </option>
        </template>
        <slot v-else />
      </select>

      <ChevronDown
        class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
        aria-hidden="true"
      />
    </div>

    <!-- MODO FILTRABLE (combobox con búsqueda) -->
    <div v-else ref="rootRef" class="relative">
      <component
        :is="icon || Search"
        class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
        aria-hidden="true"
      />

      <input
        :id="fieldId"
        ref="inputRef"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :title="selectedLabel || undefined"
        :class="inputClass"
        @input="handleInput"
        @focus="openDropdown(); $emit('focus', $event)"
        @blur="$emit('blur', $event)"
        @click="openDropdown"
        @keydown="onKeydown"
      />

      <button
        v-if="!disabled && !multiple && (hasSelection || searchQuery)"
        type="button"
        :aria-label="searchQuery ? 'Limpiar búsqueda' : 'Limpiar selección'"
        class="absolute right-9 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        @click="clearSelection"
      >
        <X class="w-3.5 h-3.5" />
      </button>

      <button
        type="button"
        :disabled="disabled"
        :aria-label="isOpen ? 'Cerrar lista' : 'Abrir lista'"
        class="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        @click="toggleDropdown"
      >
        <ChevronDown :class="['w-4 h-4 transition-transform', isOpen ? 'rotate-180' : '']" />
      </button>

      <!-- Chips (multiselección) -->
      <div v-if="multiple && selectedValues.length" class="flex flex-wrap gap-1.5 mt-2">
        <span
          v-for="value in selectedValues"
          :key="String(value)"
          class="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 text-xs font-semibold text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/20 border border-brand-100 dark:border-brand-800 rounded-full"
        >
          {{ chipLabel(value) }}
          <button
            type="button"
            :disabled="disabled"
            :aria-label="`Quitar ${chipLabel(value)}`"
            class="p-0.5 rounded-full hover:bg-brand-100 dark:hover:bg-brand-800 transition-colors"
            @click.stop="removeChip(value)"
          >
            <X class="w-3 h-3" />
          </button>
        </span>
      </div>

      <!-- Lista desplegable -->
      <div
        v-if="isOpen"
        class="absolute z-30 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg max-h-60 overflow-y-auto custom-scrollbar"
      >
        <button
          v-for="(opt, i) in filteredOptions"
          :key="optKey(opt)"
          type="button"
          :disabled="optDisabled(opt)"
          :title="optLabel(opt)"
          :class="[
            'flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm transition-colors',
            isSelected(opt)
              ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 font-semibold'
              : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700',
            activeIndex === i ? 'bg-slate-100 dark:bg-slate-700' : '',
            optDisabled(opt) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          ]"
          @mousedown.prevent="selectOption(opt)"
        >
          <span class="truncate">{{ optLabel(opt) }}</span>
          <Check v-if="isSelected(opt)" class="w-4 h-4 flex-shrink-0" />
        </button>

        <div v-if="!filteredOptions.length" class="px-4 py-6 text-center">
          <Search class="mx-auto w-5 h-5 text-slate-300 mb-1" />
          <p class="text-sm text-slate-400">{{ emptyText }}</p>
        </div>
      </div>
    </div>

    <p v-if="error" class="text-xs text-red-500 mt-1">{{ error }}</p>
    <p v-else-if="hint" class="text-xs text-slate-400 mt-1">{{ hint }}</p>
  </div>
</template>